/**
 * CTRecordingModal — Record a new Controle Technique result for a vehicle
 *
 * Form: date, center, result, mileage, observations.
 * Calls POST /api/v1/ct/record (orchestrated).
 */
import React, { useState } from 'react';
import { ClipboardCheck, X } from 'lucide-react';
import { ctAPI } from '../../../api/index.js';
import { useFormat } from '../../../i18n/format.js';

const RESULTS = [
  { value: 'favorable', label: 'Favorable (aucun défaut)', color: 'text-green-700' },
  { value: 'defavorable_mineur', label: 'Défavorable — défauts mineurs', color: 'text-amber-700' },
  { value: 'defavorable_majeur', label: 'Défavorable — défauts majeurs', color: 'text-orange-700' },
  { value: 'defavorable_critique', label: 'Défavorable — défauts critiques (interdit de rouler)', color: 'text-red-700' },
  { value: 'contrevisite', label: 'Contre-visite requise (2 mois)', color: 'text-red-700' },
];

function CTRecordingModal({ isOpen, onClose, onSuccess, onMessage, vehicle }) {
  const { fmt } = useFormat();
  const today = new Date().toISOString().split('T')[0];
  const [date, setDate] = useState(today);
  const [center, setCenter] = useState('');
  const [result, setResult] = useState('');
  const [mileage, setMileage] = useState(vehicle?.mileage || '');
  const [observations, setObservations] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!date || !center || !result) return;

    setLoading(true);
    try {
      await ctAPI.record({
        vehicle_id: vehicle.id,
        inspection_date: date,
        result,
        center,
        mileage: mileage ? parseInt(mileage) : null,
        observations: observations || null,
      });

      const resultLabel = RESULTS.find(r => r.value === result)?.label || result;
      onMessage?.({
        title: 'Succès',
        message: `CT enregistré : ${resultLabel}`,
        type: 'success',
      });
      onSuccess?.();
    } catch (error) {
      console.error('CT recording error:', error);
      onMessage?.({
        title: 'Erreur',
        message: "Impossible d'enregistrer le contrôle technique",
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white p-4 rounded-t-xl flex justify-between items-center">
          <div className="flex items-center gap-2">
            <ClipboardCheck className="w-5 h-5" />
            <div>
              <h3 className="font-semibold">Enregistrer un Contrôle Technique</h3>
              <p className="text-xs text-orange-200">
                {vehicle?.make} {vehicle?.model} — {vehicle?.license_plate}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="hover:bg-orange-400 p-1 rounded">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          {/* Date + Center */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date du contrôle *</label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                max={today}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Centre de contrôle *</label>
              <input
                type="text"
                value={center}
                onChange={(e) => setCenter(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
                placeholder="Ex: Dekra Villeneuve"
                required
              />
            </div>
          </div>

          {/* Result */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Résultat du contrôle *</label>
            <div className="space-y-2">
              {RESULTS.map(({ value, label, color }) => (
                <label
                  key={value}
                  className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                    result === value
                      ? 'border-orange-500 bg-orange-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <input
                    type="radio"
                    name="ct_result"
                    value={value}
                    checked={result === value}
                    onChange={(e) => setResult(e.target.value)}
                    className="text-orange-600 focus:ring-orange-500"
                  />
                  <span className={`text-sm font-medium ${result === value ? color : 'text-gray-700'}`}>
                    {label}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Mileage */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Kilométrage</label>
            <input
              type="number"
              value={mileage}
              onChange={(e) => setMileage(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              placeholder="Ex: 85000"
              min="0"
            />
          </div>

          {/* Observations */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Observations / défauts constatés</label>
            <textarea
              value={observations}
              onChange={(e) => setObservations(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              rows={3}
              placeholder="Ex: Usure plaquettes frein avant, jeu direction..."
              maxLength={500}
            />
          </div>

          {/* Contrevisite warning */}
          {result === 'contrevisite' && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-800 font-medium">
                Contre-visite requise dans les 2 mois suivant le contrôle.
              </p>
              <p className="text-xs text-red-600 mt-1">
                Le véhicule devra repasser le contrôle avant le{' '}
                {date ? fmt.date(new Date(new Date(date).setMonth(new Date(date).getMonth() + 2))) : '...'}.
              </p>
            </div>
          )}

          {result === 'defavorable_critique' && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-800 font-medium">
                Défaut critique — véhicule interdit de circuler.
              </p>
              <p className="text-xs text-red-600 mt-1">
                Le véhicule doit être immobilisé et réparé avant de reprendre la route.
              </p>
            </div>
          )}

          {/* Submit */}
          <div className="flex justify-end gap-3 pt-2 border-t border-gray-100">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading || !date || !center || !result}
              className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition disabled:opacity-50 flex items-center gap-2"
            >
              {loading ? (
                <span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
              ) : (
                <ClipboardCheck className="w-4 h-4" />
              )}
              Enregistrer le CT
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default CTRecordingModal;
